import {connect} from 'react-redux';
import * as d3 from 'd3';
import moment from 'moment';
import BaseFeature from './BaseFeature';
import FeatureCard from './components/FeatureCard';
import {cn} from 'lib/utils';
import {ASTRO_DIM_SENSOR, ASTRO_DIM_5} from '../../../../../../utils/constantFeatures';
import {getMinutesChart} from '../../../../../../utils/constantValues';
import {getErrorIssues, getWarningIssues} from '../../../../../../utils/validationSummary';
import AstroFadeTimingCluster from './molecules/AstroFadeTimingCluster';
import SensorSettingsCluster from './molecules/SensorSettingsCluster';
import SensorTimingCluster from './molecules/SensorTimingCluster';
import DraggableAreaChart from './charts/DraggableAreaChart';
import PropertyTableTimePickerSens from './components/PropertyTableTimePickerSens';
import PropertyRadio from './modern/PropertyRadio';
import {Trans} from 'react-i18next';
import {t} from 'i18next';

const ASTRO_DIM_5_TIME_ORDER = [
    ASTRO_DIM_5.time2, ASTRO_DIM_5.time3, ASTRO_DIM_5.time4, ASTRO_DIM_5.time5,
    ASTRO_DIM_5.time6, ASTRO_DIM_5.time7, ASTRO_DIM_5.time8, ASTRO_DIM_5.time9, ASTRO_DIM_5.time10,
];

// The unoccupied (schedule) series is filled by the shared area-gradient classes, whose darkest
// stop is this orange; the legend swatch reuses it so the two stay in step.
const UNOCCUPIED_COLOR = '#E95B0D';
const OCCUPIED_COLOR = '#1f6fb2';

const ChartLegend = () => (
    <div className="flex-row"
        style={{justifyContent: 'center', gap: '2rem', paddingTop: '0.25rem', fontSize: '0.8rem'}}>
        {[
            {color: UNOCCUPIED_COLOR, label: 'Unoccupied Level (%)'},
            {color: OCCUPIED_COLOR, label: 'Occupied Level (%)'},
        ].map(({color, label}) => (
            <span key={label} style={{display: 'inline-flex', alignItems: 'center', gap: '0.4rem'}}>
                <svg width="28" height="12" aria-hidden="true">
                    <line x1="0" y1="6" x2="28" y2="6" stroke={color} strokeWidth="2"/>
                    <circle cx="14" cy="6" r="4" fill="#fff" stroke={color} strokeWidth="2"/>
                </svg>
                {label}
            </span>
        ))}
    </div>
);

class AstroDimSensor extends BaseFeature {

    constructor(props) {
        super(props);
        this.handleChange = this.handleChange.bind(this);
        this.handleChangeReferenceSchedule = this.handleChangeReferenceSchedule.bind(this);
    }

    handleChange(propertyName, value) {
        if (!propertyName) {
            return;
        }
        const name = this.getCorrectPropertyName(propertyName);
        const extraPropNames = [
            ASTRO_DIM_5.submode,
            ASTRO_DIM_5.switchOnFadeTime,
            ASTRO_DIM_5.astroDimFadeTime,
            ASTRO_DIM_5.switchOffFadeTime,
            ASTRO_DIM_5.outputLevel1, ASTRO_DIM_5.outputLevel2, ASTRO_DIM_5.outputLevel3, ASTRO_DIM_5.outputLevel4, ASTRO_DIM_5.outputLevel5,
            ASTRO_DIM_5.outputLevel6, ASTRO_DIM_5.outputLevel7, ASTRO_DIM_5.outputLevel8, ASTRO_DIM_5.outputLevel9, ASTRO_DIM_5.outputLevel10,
            ASTRO_DIM_5.time2, ASTRO_DIM_5.time3, ASTRO_DIM_5.time4, ASTRO_DIM_5.time5, ASTRO_DIM_5.time6,
            ASTRO_DIM_5.time7, ASTRO_DIM_5.time8, ASTRO_DIM_5.time9, ASTRO_DIM_5.time10,
        ];
        if (extraPropNames.includes(name)) {
            const property = this.props.extraProperties[propertyName];
            property.value = value;
            this.props.handleChangeExtraProperties(property);
        } else {
            this.props.handleChange(propertyName, value);
        }

        if (name === ASTRO_DIM_5.submode) {
            if (value === '0') {
                this.resetTimeSlotsToDefault();
            } else if (value === '1') {
                this.applyTimeBasedDefaultSpacing();
            }
        }
    }

    handleChangeReferenceSchedule(propertyName, value) {
        this.handleChange(propertyName, value);
    }

    // Same error/warning signal as BaseFeature.getClassName, minus the legacy
    // .card-feature-component base class (that one is width:fit-content, which is
    // what the modern cards are getting away from). `.danger` / `.warning` are
    // standalone border rules in stylesheet.css, so they still apply as-is.
    cardState(properties) {
        if (getErrorIssues(this.props.issues, properties).length > 0) return 'danger';
        if (getWarningIssues(this.props.issues, properties).length > 0) return 'warning';
        return '';
    }

    resetTimeSlotsToDefault() {
        // The DDF's static Default is meant to carry the RTC-based factory schedule, but importing a
        // production file overwrites it: rawconverter's setPropertyValue writes each decoded value
        // straight into the Default attribute, so after importing a time-based file valueMeta.default
        // *is* that imported time-based schedule. Falling back to it here restored 02:00..18:00 rather
        // than the RTC schedule, and those times re-anchored at 13:00 fold backwards over the day
        // boundary, which is what pushed the curve outside the axis range. So hold the RTC schedule
        // explicitly, exactly as applyTimeBasedDefaultSpacing holds the time-based one.
        const RTC_DEFAULT_TIMES = ["17:00", "17:30", "18:00", "20:00", "20:30", "21:00", "01:00", "05:00", "09:00"];
        ASTRO_DIM_5_TIME_ORDER.forEach((name, i) => this.handleChange(name, RTC_DEFAULT_TIMES[i]));
    }

    applyTimeBasedDefaultSpacing() {
        // Time-based has no DDF default of its own to fall back on (the DDF's single static
        // Default represents the RTC-based factory schedule), so hardcode an evenly-spaced (2h)
        // set of slot times here instead, just so the schedule isn't left bunched at 00:00.
        const TIME_BASED_DEFAULT_TIMES = ["02:00", "04:00", "06:00", "08:00", "10:00", "12:00", "14:00", "16:00", "18:00"];
        ASTRO_DIM_5_TIME_ORDER.forEach((name, i) => this.handleChange(name, TIME_BASED_DEFAULT_TIMES[i]));
    }

    getNumberFromHour(time, adding, submode) {
        if (submode.value === '0') {
            const momentDate = moment('1970/01/01 ' + time, 'YYYY/MM/DD HH:mm');
            momentDate.add(adding, 'minutes');
            if (momentDate.hours() < 13 && momentDate.date() === 1) {
                momentDate.add(1, 'days');
            }
            return momentDate.toDate();
        } else {
            const momentDate = moment('1970/01/01 ' + time, 'YYYY/MM/DD HH:mm');
            momentDate.add(adding, 'minutes');
            return momentDate.toDate();
        }
    }

    getDataChart(properties, submode, extraProperties) {
        const dataChart = [];

        const minHourString = submode.value === '0' ? '13:00' : '00:00';
        const maxHourString = submode.value === '0' ? '11:00' : '24:00';

        const minHour = this.getNumberFromHour(minHourString, 0, submode);
        const maxHour = this.getNumberFromHour(maxHourString, 0, submode);

        const offsetSwitchOnFadeTime = getMinutesChart(extraProperties[ASTRO_DIM_5.switchOnFadeTime].value);
        const offsetAstroDimFadeTime = getMinutesChart(extraProperties[ASTRO_DIM_5.astroDimFadeTime].value);
        const offsetSwitchOffFadeTime = submode.value === '0' ? getMinutesChart(extraProperties[ASTRO_DIM_5.switchOffFadeTime].value) : '0';

        // The driver cannot output below its minimum dimming level, so the step from off up to that
        // minimum is instantaneous and the Switch On fade runs from there to the first level - which
        // is what makes the start-up transition a visible ramp. Same convention as AstroDim.jsx.
        const minDimming = extraProperties[ASTRO_DIM_5.outputLevel1].valueMeta?.min ?? 0;

        let sectionCont = 0;

        if (submode.value !== '2') {
            dataChart.push({
                x: minHour,
                y: 0,
                section: sectionCont,
                color: '#32a852',
            });
            // No fading between 0 and the minimum dimming level.
            dataChart.push({
                x: minHour,
                y: minDimming,
                section: sectionCont,
                color: '#32a852',
            });
        } else {
            dataChart.push({
                x: new Date(1969, 11, 31, 22, 0, 0),
                y: 0,
                section: sectionCont,
                color: '#a6a832',
            });

            dataChart.push({
                x: new Date(1969, 11, 31, 22, offsetSwitchOnFadeTime, 0),
                y: extraProperties[ASTRO_DIM_5.outputLevel1].value || 0,
                section: sectionCont,
                color: '#a6a832',
            });
        }

        const refArray = [
            {x: minHourString, y: extraProperties[ASTRO_DIM_5.outputLevel1].value},
            {
                x: submode.value !== '2' ? extraProperties[ASTRO_DIM_5.time2].value : '00:00',
                y: extraProperties[ASTRO_DIM_5.outputLevel2].value
            },
            {x: extraProperties[ASTRO_DIM_5.time3].value, y: extraProperties[ASTRO_DIM_5.outputLevel3].value},
            {x: extraProperties[ASTRO_DIM_5.time4].value, y: extraProperties[ASTRO_DIM_5.outputLevel4].value},
            {x: extraProperties[ASTRO_DIM_5.time5].value, y: extraProperties[ASTRO_DIM_5.outputLevel5].value},
            {x: extraProperties[ASTRO_DIM_5.time6].value, y: extraProperties[ASTRO_DIM_5.outputLevel6].value},
            {x: extraProperties[ASTRO_DIM_5.time7].value, y: extraProperties[ASTRO_DIM_5.outputLevel7].value},
            {x: extraProperties[ASTRO_DIM_5.time8].value, y: extraProperties[ASTRO_DIM_5.outputLevel8].value},
            {x: extraProperties[ASTRO_DIM_5.time9].value, y: extraProperties[ASTRO_DIM_5.outputLevel9].value},
            {x: extraProperties[ASTRO_DIM_5.time10].value, y: extraProperties[ASTRO_DIM_5.outputLevel10].value},
            {x: maxHourString, y: extraProperties[ASTRO_DIM_5.outputLevel10].value},
        ];

        let firstPoint = 0;
        let lastPoint = refArray.length - 1;
        for (let i = 1; i < refArray.length - 1; i++) {
            const ref = refArray[i];
            const refValue = this.getNumberFromHour(ref.x, 0, submode);
            if (minHour > refValue) {
                firstPoint = i;
            }
            if (maxHour < refValue) {
                lastPoint = i;
            }
        }

        for (let i = firstPoint; i <= lastPoint; i++) {
            const ref = refArray[i];

            if (i === firstPoint) {
                dataChart.push({
                    x: this.getNumberFromHour(minHourString, submode.value !== '2' ? offsetSwitchOnFadeTime : 0, submode),
                    y: +ref.y || 0,
                    section: sectionCont,
                });
            } else if (i === lastPoint) {
                const prevRef = refArray[i - 1];

                // Same clamp as AstroDim5: keep the Switch Off ramp from starting before the last
                // step already plotted, which would fold the curve backwards and hide the fade.
                const closingRampStart = this.getNumberFromHour(maxHourString, -(+offsetSwitchOffFadeTime), submode);
                const lastPlotted = dataChart[dataChart.length - 1];

                dataChart.push({
                    x: new Date(Math.max(closingRampStart.getTime(), lastPlotted.x.getTime())),
                    y: +prevRef.y || 0,
                    section: sectionCont,
                });
            } else {
                const prevRef = refArray[i - 1];

                dataChart.push({
                    x: this.getNumberFromHour(ref.x, 0, submode),
                    y: +prevRef.y || 0,
                    section: sectionCont,
                });

                sectionCont++;

                dataChart.push({
                    x: this.getNumberFromHour(ref.x, offsetAstroDimFadeTime, submode),
                    y: +ref.y || 0,
                    section: sectionCont,
                });
            }
        }

        // Mirror of the start: the Switch Off fade has already brought the curve down to the minimum
        // dimming level, and the drop from there to off is instantaneous.
        dataChart.push({
            x: maxHour,
            y: minDimming,
        });

        dataChart.push({
            x: maxHour,
            y: 0,
        });

        return dataChart;
    }

    getOccupyOverlayDataChart(properties, submode, extraProperties) {
        const minHourString = submode.value === '0' ? '13:00' : '00:00';
        const maxHourString = submode.value === '0' ? '11:00' : '24:00';

        const minHour = this.getNumberFromHour(minHourString, 0, submode);
        const maxHour = this.getNumberFromHour(maxHourString, 0, submode);

        const refArray = [
            {x: minHourString, y: properties[ASTRO_DIM_SENSOR.occupyLevel1].value},
            {
                x: submode.value !== '2' ? extraProperties[ASTRO_DIM_5.time2].value : '00:00',
                y: properties[ASTRO_DIM_SENSOR.occupyLevel2].value,
            },
            {x: extraProperties[ASTRO_DIM_5.time3].value, y: properties[ASTRO_DIM_SENSOR.occupyLevel3].value},
            {x: extraProperties[ASTRO_DIM_5.time4].value, y: properties[ASTRO_DIM_SENSOR.occupyLevel4].value},
            {x: extraProperties[ASTRO_DIM_5.time5].value, y: properties[ASTRO_DIM_SENSOR.occupyLevel5].value},
            {x: extraProperties[ASTRO_DIM_5.time6].value, y: properties[ASTRO_DIM_SENSOR.occupyLevel6].value},
            {x: extraProperties[ASTRO_DIM_5.time7].value, y: properties[ASTRO_DIM_SENSOR.occupyLevel7].value},
            {x: extraProperties[ASTRO_DIM_5.time8].value, y: properties[ASTRO_DIM_SENSOR.occupyLevel8].value},
            {x: extraProperties[ASTRO_DIM_5.time9].value, y: properties[ASTRO_DIM_SENSOR.occupyLevel9].value},
            {x: extraProperties[ASTRO_DIM_5.time10].value, y: properties[ASTRO_DIM_SENSOR.occupyLevel10].value},
            {x: maxHourString, y: properties[ASTRO_DIM_SENSOR.occupyLevel10].value},
        ];

        let firstPoint = 0;
        let lastPoint = refArray.length - 1;
        for (let i = 1; i < refArray.length - 1; i++) {
            const refValue = this.getNumberFromHour(refArray[i].x, 0, submode);
            if (minHour > refValue) {
                firstPoint = i;
            }
            if (maxHour < refValue) {
                lastPoint = i;
            }
        }

        // The sensor's Fading In / Fading Out times shape only the two ends of this curve: it ramps
        // up from the baseline at the start of the schedule and back down to it at the end. The
        // level changes in between are instantaneous steps - the fade times are not applied to them.
        const fadeInMinutes = +getMinutesChart(properties[ASTRO_DIM_SENSOR.fadingInTime].value) || 0;
        const fadeOutMinutes = +getMinutesChart(properties[ASTRO_DIM_SENSOR.fadingOutTime].value) || 0;

        const level = (i) => +refArray[i].y || 0;
        const shift = (date, minutes) => new Date(date.getTime() + minutes * 60000);

        // Every point at which the occupied level changes, including leaving the baseline at the
        // start of the schedule and returning to it at the end.
        const transitions = [{t: minHour, from: 0, to: level(firstPoint)}];
        for (let i = firstPoint + 1; i < lastPoint; i++) {
            transitions.push({
                t: this.getNumberFromHour(refArray[i].x, 0, submode),
                from: level(i - 1),
                to: level(i),
            });
        }
        transitions.push({
            t: maxHour,
            from: level(Math.max(lastPoint - 1, firstPoint)),
            to: 0,
            endsAtAxis: true,
        });

        const last = transitions.length - 1;

        // Both ramps are clamped to their neighbouring transition, so a fade longer than the gap
        // cannot overrun it and send the curve backwards. With no intermediate breakpoints at all
        // the two would clamp against each other, hence the final guard that the fade-out cannot
        // begin before the fade-in has finished.
        const firstStep = last > 1 ? transitions[1].t : transitions[last].t;
        const lastStep = last > 1 ? transitions[last - 1].t : transitions[0].t;

        const fadeInEnd = new Date(Math.min(
            shift(transitions[0].t, fadeInMinutes).getTime(), firstStep.getTime()));
        const fadeOutStart = new Date(Math.max(
            shift(transitions[last].t, -fadeOutMinutes).getTime(), lastStep.getTime(), fadeInEnd.getTime()));

        const overlay = [];
        transitions.forEach((tr, k) => {
            if (k === 0) {
                // Leaves the baseline over Fading In.
                overlay.push({x: tr.t, y: tr.from});
                overlay.push({x: fadeInEnd, y: tr.to});
            } else if (k === last) {
                // Holds the last level, then returns to the baseline over Fading Out.
                overlay.push({x: fadeOutStart, y: tr.from});
                overlay.push({x: tr.t, y: tr.to});
            } else {
                // A repeated x under linear interpolation renders as the vertical step it is.
                overlay.push({x: tr.t, y: tr.from});
                overlay.push({x: tr.t, y: tr.to});
            }
        });

        return overlay;
    }

    getXLabels(dataChart, submode) {
        const labels = [];
        const smallLabels = [];
        const ticks = [];

        if (submode.value === '2') {
            ticks.push(new Date(1970, 0, 1, 0, 0, 0));
            labels.push({label: 'SD', x: new Date(1970, 0, 1, 0, 0, 0)});
        }

        return {labels: labels, smallLabels: smallLabels, ticks: ticks};
    }

    render() {
        const {properties, extraProperties} = this.props;

        const submode = extraProperties[ASTRO_DIM_5.submode];
        const switchOnFadeTime = extraProperties[ASTRO_DIM_5.switchOnFadeTime];
        const astroDimFadeTime = extraProperties[ASTRO_DIM_5.astroDimFadeTime];
        const switchOffFadeTime = extraProperties[ASTRO_DIM_5.switchOffFadeTime];

        // Mirrors AstroDim5: Astro-5 runs off the driver's internal RTC with no geolocation, so the
        // astro/midnight marker is meaningless in the asttm-family opmodes this component serves.
        const isAsttm = this.props.opmodesSelected && (
            this.props.opmodesSelected.name === 'ast-5' ||
            this.props.opmodesSelected.name === 'astdimsens' ||
            this.props.opmodesSelected.name === 'astroover'
        );
        const displayLocation = !isAsttm && submode.value === '1';

        // Silent/occupy dimming levels are deliberately not read here: in astdimsens the unoccupied
        // and occupied levels are set per time slot in the Reference Schedule, so the two global
        // levels don't apply to this opmode.
        const triggerVoltage = properties[ASTRO_DIM_SENSOR.triggerVoltage];
        const detectionLogic = properties[ASTRO_DIM_SENSOR.detectionLogic];
        const fadingInTime = properties[ASTRO_DIM_SENSOR.fadingInTime];
        const holdUpTime = properties[ASTRO_DIM_SENSOR.holdUpTime];
        const fadingOutTime = properties[ASTRO_DIM_SENSOR.fadingOutTime];

        // Chart configuration
        const min = submode.value === '0'
            ? {x: new Date(1970, 0, 1, 12, 0, 0)}
            : (submode.value === '1' ? {x: new Date(1970, 0, 1, 0, 0, 0)} : {x: new Date(1969, 11, 31, 22, 0, 0)});
        const max = submode.value === '0'
            ? {x: new Date(1970, 0, 2, 12, 0, 0), y: 100}
            : {x: new Date(1970, 0, 2, 0, 0, 0), y: 100};

        // RTC-based ticks are literal clock hours (centered on the 13:00 restart). Time-based/SD
        // slot values are elapsed durations since the schedule start, not times of day, so they're
        // labelled "+Nh" from that start rather than reusing the clock-hour format.
        const tickFormat = {
            x: submode.value === '0'
                ? d3.timeFormat('%H')
                : (d) => Math.round(moment(d).diff(moment(min.x), 'hours', true)) + 'h',
        };

        const time = [];
        const tickValues = {x: time};
        if (submode.value === '0') {
            for (let i = 12; i < 24; i++) {
                time.push(new Date(1970, 0, 1, i, 0, 0));
            }
            for (let i = 0; i < 13; i++) {
                time.push(new Date(1970, 0, 2, i, 0, 0));
            }
        } else if (submode.value === '1') {
            for (let i = 0; i < 24; i++) {
                time.push(new Date(1970, 0, 1, i, 0, 0));
            }
            time.push(new Date(1970, 0, 2, 0, 0, 0));
        } else if (submode.value === '2') {
            for (let i = 1; i < 24; i++) {
                time.push(new Date(1970, 0, 1, i, 0, 0));
            }
            time.push(new Date(1970, 0, 2, 0, 0, 0));
        }

        const dataChart = this.getDataChart(properties, submode, extraProperties);
        const overlayDataChart = this.getOccupyOverlayDataChart(properties, submode, extraProperties);

        // Generated generously (rather than a fixed-size literal) so it can never run out of
        // alternating light/dark sections regardless of how many fade-time transitions the
        // schedule produces - same rationale as AstroDim5.
        const sections = {};
        for (let i = 0; i <= 40; i++) {
            sections[i] = i % 2 === 0 ? 'area-dark' : 'area-light';
        }

        return (
            // Content is centred with gutters on wide screens. This wraps the page
            // content only — the app shell (header / feature sidebar) is untouched.
            <div className="mx-auto w-full max-w-[1800px] px-4 pb-4">
                {/* 1 column on phones, 3 on tablets, 12 on desktop. Every card declares
                    its own span, so reordering or adding one needs no other change and
                    there are no inline widths or minWidths fighting the grid. */}
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 xl:grid-cols-12">

                    <FeatureCard
                        variant="modern"
                        title={<Trans i18nKey="Submode"/>}
                        bodyLayout="fields"
                        className={cn('lg:col-span-1 xl:col-span-2', this.cardState([submode]))}>
                        <PropertyRadio
                            property={submode}
                            isChecked={submode.value === '0'}
                            valueForTrue={'0'}
                            title={<Trans i18nKey="RTC_Based"/>}
                            handleChange={this.handleChange}
                        />
                        <PropertyRadio
                            disabled={submode.valueMeta.max === '0'}
                            property={submode}
                            isChecked={submode.value === '1'}
                            valueForTrue={'1'}
                            title={<Trans i18nKey="Time_Based"/>}
                            handleChange={this.handleChange}
                        />
                        <PropertyRadio
                            disabled={!this.props.opmodesSelected?.name?.includes('pd')}
                            property={submode}
                            isChecked={submode.value === '2'}
                            valueForTrue={'2'}
                            title={<Trans i18nKey="SD_triggered"/>}
                            handleChange={this.handleChange}
                        />
                    </FeatureCard>

                    <AstroFadeTimingCluster
                        variant="modern"
                        switchOnFadeTime={switchOnFadeTime}
                        astroDimFadeTime={astroDimFadeTime}
                        switchOffFadeTime={switchOffFadeTime}
                        showSwitchOff={submode && submode.value === '0'}
                        className={cn('lg:col-span-1 xl:col-span-3', this.cardState([switchOnFadeTime, astroDimFadeTime, switchOffFadeTime]))}
                        handleChange={this.handleChange}/>

                    <SensorSettingsCluster
                        variant="modern"
                        triggerVoltage={triggerVoltage}
                        detectionLogic={detectionLogic}
                        className={cn('lg:col-span-1 xl:col-span-4', this.cardState([triggerVoltage, detectionLogic]))}
                        handleChange={this.handleChange}/>

                    <SensorTimingCluster
                        variant="modern"
                        fadingInTime={fadingInTime}
                        holdUpTime={holdUpTime}
                        fadingOutTime={fadingOutTime}
                        className={cn('lg:col-span-1 xl:col-span-3', this.cardState([fadingInTime, holdUpTime, fadingOutTime]))}
                        handleChange={this.handleChange}/>

                    <FeatureCard
                        variant="modern"
                        title={<Trans i18nKey="Reference_Schedule"/>}
                        className={cn('lg:col-span-3 xl:col-span-12', this.cardState(this.props.properties))}
                        // The 10-slot table cannot usefully reflow below a certain width,
                        // so it scrolls inside its own card rather than forcing the whole
                        // page to overflow the way the old minWidth:900px did.
                        bodyClassName="overflow-x-auto">
                        <PropertyTableTimePickerSens
                            properties={{...this.props.properties, ...this.props.extraProperties}}
                            submode={submode}
                            handleChange={this.handleChangeReferenceSchedule}
                            sizeTitle={2}
                            sizePickers={2}
                            sizeMeasureUnit={2}
                            displayColors={false}
                            firstRow={{
                                title: <Trans i18nKey="Output_Level"/>,
                                property: ASTRO_DIM_5.outputLevel,
                                measureUnit: '%'
                            }}
                            secondRow={{
                                title: <Trans i18nKey="Time"/>,
                                property: ASTRO_DIM_5.time,
                                measureUnit: 'hh:mm'
                            }}
                            thirdRow={{
                                title: 'Sensor Occupy Level',
                                property: ASTRO_DIM_SENSOR.occupyLevel,
                                measureUnit: '%'
                            }}
                        />
                    </FeatureCard>

                    <FeatureCard
                        variant="modern"
                        title={<Trans i18nKey="Graph"/>}
                        className="lg:col-span-3 xl:col-span-12">
                        <DraggableAreaChart
                            containsErrors={getErrorIssues(this.props.issues, this.props.properties).length > 0}
                            properties={this.props.properties}
                            dataChart={dataChart}
                            min={min}
                            max={max}
                            tickFormat={tickFormat}
                            tickValues={tickValues}
                            xLabels={this.getXLabels(dataChart, submode)}
                            x={{title: t(submode.value === '0' ? "Time_h" : "Time_Elapsed_h")}}
                            y={{title: t("Output_Level_Percent")}}
                            sections={sections}
                            overlayDataChart={overlayDataChart}
                            overlayColor={OCCUPIED_COLOR}
                            overlayStrokeWidth={3.5}
                            overlayAreaGradient={true}
                            xThresholds={
                                displayLocation
                                    ? [{
                                        // Elapsed axis runs exactly [start, start+24h) here, so real midnight
                                        // sits midnightShift minutes after the start, not a whole day later.
                                        value: new Date(1970, 0, 1, 0, this.props.midnightShift, 0),
                                        color: 'rgb(0,0,255)'
                                    }]
                                    : null
                            }
                            handleChange={this.handleChartChange}/>
                        <ChartLegend/>
                    </FeatureCard>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    issues: state.project.issueByProperty,
    midnightShift: state.project.midnightShift,
    opmodesSelected: state.driver.opmodesSelected,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(AstroDimSensor);
